import { createFileRoute, Link } from "@tanstack/react-router";
import {
  pricingBreadcrumbJsonLd,
  pricingJsonLd,
  pricingRouteHead,
} from "@/lib/pricing-seo";
import { jsonLdScript } from "@/lib/marketing-jsonld";
import {
  FAQ,
  PLATFORM_PAGE,
  PRICING_PAGE,
  type FaqEntry,
} from "@/content/marketing";
import { Calculator } from "@/components/landing/Calculator";
import { Faq } from "@/components/landing/Faq";
import { ArrowDown, ArrowRight, Check } from "lucide-react";
import { StartFreeCta } from "@/components/landing/StartFreeCta";
import {
  BAND_PRICE,
  FREE_LIMIT,
  figure,
  emailsPerMonth,
  goodsenderMonthly,
} from "@/content/marketing/pricing";
import "@/styles/marketing-pages.css";
import { StackCost } from "@/components/landing/StackCost";

// Head/meta AND structured data for this route are owned by
// `src/lib/pricing-seo.ts` — a route module may only export components, so
// keeping the JSON-LD there is also what makes it directly assertable.
export const Route = createFileRoute("/_home/pricing")({
  ssr: true, // public SEO route (D-216-5)
  head: pricingRouteHead,
  component: PricingPage,
});

/*
 * Mapped, not filtered: `faqIds` also fixes the order (pricing leads its own
 * page's section, whatever the module order is).
 *
 * An id that resolves to nothing throws instead of dropping out of the list.
 * The ids and the entries live in two content modules now, so a rename on one
 * side used to render the section one question short with no signal anywhere
 * (#792 review). `content.test.ts`'s "resolves every id the pricing page names
 * to an FAQ entry" is what makes this branch unreachable rather than a 500
 * waiting to happen; it is here so a change that gets past that test cannot be
 * quiet about it. Delete the test and this throw becomes a live render time
 * crash, so they move together.
 */
function resolveFaq(ids: readonly string[]): FaqEntry[] {
  return ids.map((id) => {
    const entry = FAQ.find((f) => f.id === id);
    if (!entry) {
      throw new Error(`PRICING_PAGE.faqIds names an id absent from FAQ: ${id}`);
    }
    return entry;
  });
}

function PricingPage() {
  const c = PRICING_PAGE;
  const included = PLATFORM_PAGE.included;
  const pricingFaq = resolveFaq(PRICING_PAGE.faqIds);
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLdScript(pricingJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: jsonLdScript(pricingBreadcrumbJsonLd),
        }}
      />
      <nav aria-label="Breadcrumb" className="sr-only">
        <ol>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li aria-current="page">Pricing</li>
        </ol>
      </nav>
      {/* Top-level landmark so assistive tech can skip the nav (#416); the
          layout's SiteFooter is a sibling, so contentinfo survives. */}
      <main className="gs-marketing-page gs-pricing-page">
        <section className="gs-marketing-hero gs-pricing-hero">
          <p className="gs-eyebrow">{c.eyebrow}</p>
          <h1>
            {c.title}
            <br />
            <span>{c.titleAccent}</span>
          </h1>
          <p className="gs-marketing-lede">{c.lede}</p>
          <div className="gs-price-sheet">
            <div className="gs-free-allowance">
              <span className="gs-eyebrow">{c.allowance}</span>
              <strong>{figure(FREE_LIMIT)}</strong>
              <p>{c.allowanceNote}</p>
              <div>
                <span>{c.freeLabel}</span>
                <StartFreeCta location="pricing_free_plan" />
              </div>
              <p>{c.freeNote}</p>
            </div>
            <div className="gs-paid-bands">
              <h2>{c.paidLabel}</h2>
              <dl>
                <div>
                  <dt>{c.firstPaid}</dt>
                  <dd>
                    ${BAND_PRICE}
                    <small>/mo</small>
                  </dd>
                </div>
                <div>
                  <dt>{c.nextPaid}</dt>
                  <dd>
                    +${BAND_PRICE}
                    <small>/mo</small>
                  </dd>
                </div>
              </dl>
              <p>{c.boundaryNote}</p>
              <small>{c.currency}</small>
            </div>
          </div>
          <a href="#included" className="gs-text-link">
            {c.includedLink}
            <ArrowDown size={15} aria-hidden />
          </a>
        </section>
        <section className="gs-marketing-section gs-pricing-examples">
          <div className="gs-marketing-heading">
            <h2>{c.examplesTitle}</h2>
            <p>{c.examplesLede}</p>
          </div>
          <div className="gs-send-examples">
            {c.exampleSubscribers.map((subscribers, i) => {
              const emails = emailsPerMonth(subscribers, c.examplesCadence);
              const price = goodsenderMonthly(emails);
              return (
                <div key={subscribers}>
                  <p className="gs-example-name">{c.examplesLabels[i]}</p>
                  <p>
                    <strong>{figure(subscribers)}</strong> {c.subscribersUnit}
                  </p>
                  <div className="gs-volume-track" aria-hidden>
                    <span
                      style={{
                        width: `${(emails / emailsPerMonth(Math.max(...c.exampleSubscribers), c.examplesCadence)) * 100}%`,
                      }}
                    />
                  </div>
                  <p>
                    {figure(emails)} {c.sendsUnit}
                  </p>
                  <span className="gs-example-price">
                    {price === 0 ? c.freeLabel : `$${price}`}
                    <small>{price === 0 ? "" : " /mo"}</small>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
        <section className="gs-marketing-section gs-calculator-section">
          <div>
            <p className="gs-eyebrow">{c.calculator.yoursLabel}</p>
            <h2>{c.calculatorTitle}</h2>
            <p>{c.calculatorLede}</p>
          </div>
          <Calculator />
        </section>
        <section
          id="included"
          className="gs-marketing-section gs-included-section"
        >
          <div className="gs-marketing-heading">
            <div>
              <p className="gs-eyebrow">{included.eyebrow}</p>
              <h2>{included.title}</h2>
            </div>
            <p>{included.lede}</p>
          </div>
          <div className="gs-included-list">
            {included.items.map((item, i) => (
              <div key={item}>
                <Check size={18} aria-hidden />
                <div>
                  <h3>{included.labels[i]}</h3>
                  <p>{item}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="gs-included-details">
            <h3>{included.detailsTitle}</h3>
            <p>{included.domain}</p>
            <p>{included.consent}</p>
            <p>{included.formats}</p>
            <p>{included.portability}</p>
          </div>
          <p className="gs-compare-note">
            {c.compareNote}{" "}
            <Link to="/compare" className="gs-text-link">
              {c.compareLink}
              <ArrowRight size={15} aria-hidden />
            </Link>
          </p>
        </section>
        <details className="gs-stack-details">
          <summary>{c.stack.title}</summary>
          <StackCost />
        </details>
        <Faq title={PRICING_PAGE.faqTitle} entries={pricingFaq} />
      </main>
    </>
  );
}
